import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { retry, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { AlertService } from '@app/_services';
import { GuestRefundReason } from './guest-refund-reason';
import { SharedService } from './../_shared/shared.service';

@Injectable({ providedIn: 'root' })
export class GuestRefundReasonService {

	statuses: any = [
		{ value: '0', name: 'Active' },
		{ value: '2', name: 'Suspended' },
		{ value: '1', name: 'Canceled' }
	];
	
	reasonCodes: any[] = [
		{ value: '10', name: 'Linens or towels insufficient' },
		{ value: '11', name: 'Trash not removed' },
		{ value: '12', name: 'Unit was not clean' },
		{ value: '20', name: 'Appliance malfunction' },
		{ value: '21', name: 'Broken furniture' },
		{ value: '22', name: 'Electrical issue' },
		{ value: '23', name: 'HVAC or climate control failure' },
		{ value: '24', name: 'Pest or bug issue' },
		{ value: '25', name: 'Plumbing issue' },
		{ value: '30', name: 'Amenity not working (hot tub, pool, sauna, fireplace)' },
		{ value: '31', name: 'BBQ grill not functional' },
		{ value: '32', name: 'Parking unavailable' },
		{ value: '33', name: 'Wi-Fi or TV outage' },
		{ value: '40', name: 'Inaccurate listing' },
		{ value: '41', name: 'Sleeping capacity not as listed' },
		{ value: '42', name: 'View obstructed or misrepresented' },
		{ value: '50', name: 'Elevator out of service' },
		{ value: '51', name: 'Shared resort amenity closed' },
		{ value: '60', name: 'Construction or excessive noise' },
		{ value: '61', name: 'Early departure due to uninhabitable conditions' },
		{ value: '62', name: 'Severe weather event' },
		{ value: '63', name: 'Utility outage (area-wide)' },
	];

	reasonCodeGroups: any[] = [
		{ label: 'Housekeeping', items: [
			{ value: '10', label: 'Linens or towels insufficient' },
			{ value: '11', label: 'Trash not removed' },
			{ value: '12', label: 'Unit was not clean' },
		]},
		{ label: 'Maintenance', items: [
			{ value: '20', label: 'Appliance malfunction' },
			{ value: '21', label: 'Broken furniture' },
			{ value: '22', label: 'Electrical issue' },
			{ value: '23', label: 'HVAC or climate control failure' },
			{ value: '24', label: 'Pest or bug issue' },
			{ value: '25', label: 'Plumbing issue' },
		]},
		{ label: 'Amenities', items: [
			{ value: '30', label: 'Amenity not working (hot tub, pool, sauna, fireplace)' },
			{ value: '31', label: 'BBQ grill not functional' },
			{ value: '32', label: 'Parking unavailable' },
			{ value: '33', label: 'Wi-Fi or TV outage' },
		]},
		{ label: 'Listing Quality (As Advertised)', items: [
			{ value: '40', label: 'Inaccurate listing' },
			{ value: '41', label: 'Sleeping capacity not as listed' },
			{ value: '42', label: 'View obstructed or misrepresented' },
		]},
		{ label: 'Resort Issues', items: [
			{ value: '50', label: 'Elevator out of service' },
			{ value: '51', label: 'Shared resort amenity closed' },
		]},
		{ label: 'Region Issues', items: [
			{ value: '60', label: 'Construction or excessive noise' },
			{ value: '61', label: 'Early departure due to uninhabitable conditions' },
			{ value: '62', label: 'Severe weather event' },
			{ value: '63', label: 'Utility outage (area-wide)' },
		]},
	];


	constructor
		(
			private http: HttpClient,
			private alertService: AlertService,
			private sharedService: SharedService,
		) { }

	urlEndpoint = 'guestRefundReasons';

	add(guestRefundReason: GuestRefundReason): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(guestRefundReason));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, guestRefundReason);
	}

	update(guestRefundReason: GuestRefundReason): Observable<ApiResponse> {
		return this.http.patch<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, guestRefundReason);
	}

	delete(developmentId: number): Observable<ApiResponse> {
		return this.http.delete<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${developmentId}`);
	}

	findAll(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse> {
		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		console.log('posting... ' + JSON.stringify(filters));
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));

	}

	findById(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findByGuestRefundId(guestRefundId: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/byRefundId/${guestRefundId}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	audit(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}/audit`).pipe(retry({ count: 2, delay: 1500 }));
	}


	prepareParamsFromPrimeNgDataTable(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any) {
		try {
			console.log(
				'  |  page:' + page
				+ '  |  size:' + size
				+ '  |  sortBy:' + sortBy
				+ '  |  sortOrder:' + sortOrder
				+ '  |  multiSortMeta:' + JSON.stringify(multiSortMeta)
				+ '  |  globalFilter:' + globalFilter
				+ '  |  filters:' + JSON.stringify(filters)
			);

			let params = new HttpParams();

			if (!isNaN(page)) { params = params.append('page', page); }
			if (!isNaN(size)) { params = params.append('size', size); }
			if (sortBy) { params = params.append('sort', sortBy + ',' + (sortOrder === 1 ? 'asc' : 'desc')); }
			if (multiSortMeta) {
				// if sortMode="multiple" in primeng table then multiSortMeta is used for ALL sorting (even just one column.)
				// Otherwise, single sorting is used above.
				// event.multiSortMeta:[{"field":"lastName","order":1},{"field":"firstName","order":1}]
				for (let i = 0; i < multiSortMeta.length; i++) {
					let json = JSON.parse(JSON.stringify(multiSortMeta[i]));
					params = params.append('sort', json.field + ',' + (json.order === 1 ? 'asc' : 'desc'));
				}
			}
			// globalFilter works, but did not implemnt in Spring but not hard to write custom findAll and specify certain columns to look.
			if (globalFilter) { params = params.append('filter', globalFilter); }

			// multiselect or input or whatever... these work slick.
			if (filters) {
				// key:status val:{"value":["A"],"matchMode":"in"}
				for (let i = 0, keys = Object.keys(filters), ii = keys.length; i < ii; i++) {
					console.log('key:' + keys[i] + ' val:' + JSON.stringify(filters[keys[i]]));
					params = params.append(keys[i], filters[keys[i]].value);
				}
			}
			return params;
		} catch (error) {
			console.log('prepareParamsFromPrimeNgDataTable()' + error);
		}
	}
}
